import * as THREE from 'three';
import { RevolveAngleGizmoPartKind } from '../../../mesh/feature/revolve/RevolveAngleGizmoPartKind';
import type {
    RevolveAngleDragContour,
    RevolveDraggedStateInput,
    RevolveDraggedStateResult,
} from './RevolveAngleDragTypes';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/interaction/revolve 层；
 * 2. 负责旋转角度拖拽所需的纯数学函数；
 * 3. 该文件不访问 DOM、Viewer 或 Worker，便于单元测试覆盖角度规则。
 */

/** 方向 1 最小角度，避免 Puppy CAD 内核 构造退化旋转。 */
const MIN_DIRECTION_1_ANGLE = 1e-4;
/** 最大旋转角。 */
const MAX_ANGLE = Math.PI * 2;

/**
 * 归一化轴向量。
 *
 * @param axisDirection 输入轴方向。
 * @returns 单位轴方向。
 */
export function normalizeRevolveAxisDirection(axisDirection: THREE.Vector3): THREE.Vector3 {
    if (axisDirection.lengthSq() <= 1e-12) {
        return new THREE.Vector3(0, 0, 1);
    }
    return axisDirection.clone().normalize();
}

/**
 * 选择与轴垂直的回退方向。
 *
 * @param axisDirection 轴方向。
 * @returns 垂直方向。
 */
export function chooseRevolveOrthogonalDirection(axisDirection: THREE.Vector3): THREE.Vector3 {
    const axis = normalizeRevolveAxisDirection(axisDirection);
    const fallback = Math.abs(axis.z) < 0.9
        ? new THREE.Vector3(0, 0, 1)
        : new THREE.Vector3(0, 1, 0);
    const result = fallback.clone().cross(axis);
    if (result.lengthSq() <= 1e-12) {
        return new THREE.Vector3(1, 0, 0);
    }
    return result.normalize();
}

/**
 * 将方向投影到旋转轴法平面。
 *
 * @param direction 输入方向。
 * @param axisDirection 轴方向。
 * @returns 投影后的单位方向。
 */
export function projectRevolveDirectionToAxisPlane(
    direction: THREE.Vector3,
    axisDirection: THREE.Vector3,
): THREE.Vector3 {
    const axis = normalizeRevolveAxisDirection(axisDirection);
    const projected = direction.clone().sub(axis.clone().multiplyScalar(direction.dot(axis)));
    if (projected.lengthSq() <= 1e-12) {
        return chooseRevolveOrthogonalDirection(axis);
    }
    return projected.normalize();
}

/**
 * 计算轮廓中心。
 *
 * @param contours 轮廓集合。
 * @returns 轮廓包围盒中心。
 */
export function computeRevolveContoursCenter(contours: RevolveAngleDragContour[]): THREE.Vector3 {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;

    contours.forEach((contour) => {
        contour.outer.forEach((point) => {
            minX = Math.min(minX, point[0]);
            minY = Math.min(minY, point[1]);
            minZ = Math.min(minZ, point[2]);
            maxX = Math.max(maxX, point[0]);
            maxY = Math.max(maxY, point[1]);
            maxZ = Math.max(maxZ, point[2]);
        });
    });

    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
        return new THREE.Vector3();
    }
    return new THREE.Vector3(
        (minX + maxX) * 0.5,
        (minY + maxY) * 0.5,
        (minZ + maxZ) * 0.5,
    );
}

/**
 * 计算轮廓到轴的最大半径。
 *
 * @param contours 轮廓集合。
 * @param axisCenter 轴中心点。
 * @param axisDirection 轴方向。
 * @returns 最大半径。
 */
export function computeRevolveMaxContourRadius(
    contours: RevolveAngleDragContour[],
    axisCenter: THREE.Vector3,
    axisDirection: THREE.Vector3,
): number {
    const axis = normalizeRevolveAxisDirection(axisDirection);
    let maxRadius = 0;
    contours.forEach((contour) => {
        contour.outer.forEach((point) => {
            const worldPoint = new THREE.Vector3(point[0], point[1], point[2]);
            const radial = worldPoint.clone().sub(axisCenter);
            radial.sub(axis.clone().multiplyScalar(radial.dot(axis)));
            maxRadius = Math.max(maxRadius, radial.length());
        });
    });
    return maxRadius;
}

/**
 * 计算屏幕像素对应的世界尺寸。
 *
 * @param camera 相机。
 * @param container 容器。
 * @param worldPoint 世界点。
 * @param pixelSize 像素尺寸。
 * @returns 世界尺寸。
 */
export function computeRevolveWorldSizeFromPixels(
    camera: THREE.Camera,
    container: HTMLElement,
    worldPoint: THREE.Vector3,
    pixelSize: number,
): number {
    const viewportHeight = Math.max(1, container.clientHeight);
    if (camera instanceof THREE.PerspectiveCamera) {
        const distance = Math.max(1e-6, camera.position.distanceTo(worldPoint));
        const fovRad = THREE.MathUtils.degToRad(camera.fov);
        const worldHeight = 2 * Math.tan(fovRad * 0.5) * distance;
        return (worldHeight / viewportHeight) * pixelSize;
    }
    if (camera instanceof THREE.OrthographicCamera) {
        const worldHeight = (camera.top - camera.bottom) / Math.max(1e-6, camera.zoom);
        return (worldHeight / viewportHeight) * pixelSize;
    }
    return pixelSize * 0.01;
}

/**
 * 绕轴旋转点。
 *
 * @param point 输入点。
 * @param axisOrigin 轴原点。
 * @param axisDirection 轴方向。
 * @param angle 旋转角。
 * @returns 旋转后的点。
 */
export function rotateRevolvePointAroundAxis(
    point: THREE.Vector3,
    axisOrigin: THREE.Vector3,
    axisDirection: THREE.Vector3,
    angle: number,
): THREE.Vector3 {
    const axis = normalizeRevolveAxisDirection(axisDirection);
    const quaternion = new THREE.Quaternion().setFromAxisAngle(axis, angle);
    return point.clone().sub(axisOrigin).applyQuaternion(quaternion).add(axisOrigin);
}

/**
 * 解析有符号角。
 *
 * @param axisDirection 轴方向。
 * @param baseDirection 基准方向。
 * @param targetDirection 目标方向。
 * @returns 有符号角。
 */
export function resolveRevolveSignedAngle(
    axisDirection: THREE.Vector3,
    baseDirection: THREE.Vector3,
    targetDirection: THREE.Vector3,
): number {
    const axis = normalizeRevolveAxisDirection(axisDirection);
    const from = projectRevolveDirectionToAxisPlane(baseDirection, axis);
    const to = projectRevolveDirectionToAxisPlane(targetDirection, axis);
    /**
     * 这里必须使用 atan2，而不是 Vector3.angleTo：
     * 1. angleTo 只能返回 0..π，跨过 180° 后会丢失整圈拖拽方向；
     * 2. 旧分支已按 SolidWorks 行为支持 0..360° 连续拖拽；
     * 3. atan2(sin, cos) 可稳定保留旋转方向，再交给 unwrap 贴近上一帧角度。
     */
    const sinValue = axis.dot(from.clone().cross(to));
    const cosValue = THREE.MathUtils.clamp(from.dot(to), -1, 1);
    return Math.atan2(sinValue, cosValue);
}

/**
 * 解析拖拽后的旋转角度状态。
 *
 * @param input 拖拽输入。
 * @returns 拖拽状态。
 */
export function resolveDraggedRevolveState(input: RevolveDraggedStateInput): RevolveDraggedStateResult {
    const {
        dragPart,
        signedAngle,
        direction1Angle,
        directionReversed,
        direction2Enabled,
        direction2Angle,
    } = input;

    if (dragPart === RevolveAngleGizmoPartKind.Direction1Head) {
        const unwrappedAngle = unwrapAngleNearReference(signedAngle, direction1Angle);
        const nextReversed = unwrappedAngle < 0 ? !directionReversed : directionReversed;
        const normalizedAngle = nextReversed !== directionReversed
            ? Math.abs(unwrappedAngle)
            : normalizeCounterClockwiseAngleFromSigned(unwrappedAngle);
        return {
            direction1Angle: Math.min(MAX_ANGLE, Math.max(MIN_DIRECTION_1_ANGLE, normalizedAngle)),
            directionReversed: nextReversed,
            direction2Enabled,
            direction2Angle,
        };
    }

    if (dragPart === RevolveAngleGizmoPartKind.Direction2Head) {
        const clockwiseSignedAngle = -signedAngle;
        const unwrappedAngle = unwrapAngleNearReference(clockwiseSignedAngle, direction2Angle);
        const nextReversed = unwrappedAngle < 0 ? !directionReversed : directionReversed;
        const normalizedAngle = nextReversed !== directionReversed
            ? Math.abs(unwrappedAngle)
            : normalizeClockwiseAngleFromSigned(-unwrappedAngle);
        return {
            direction1Angle,
            directionReversed: nextReversed,
            direction2Enabled: true,
            direction2Angle: Math.min(MAX_ANGLE, Math.max(0, normalizedAngle)),
        };
    }

    return {
        direction1Angle,
        directionReversed,
        direction2Enabled,
        direction2Angle,
    };
}

/**
 * 将顺时针方向转换为正角。
 *
 * @param signedAngle 有符号角。
 * @returns 归一化角度。
 */
function normalizeCounterClockwiseAngleFromSigned(signedAngle: number): number {
    let angle = signedAngle;
    if (angle <= 0) {
        angle += MAX_ANGLE;
    }
    return Math.min(MAX_ANGLE, Math.max(MIN_DIRECTION_1_ANGLE, angle));
}

/**
 * 将逆向拖拽角转换为方向 2 角度。
 *
 * @param signedAngle 有符号角。
 * @returns 方向 2 角度。
 */
function normalizeClockwiseAngleFromSigned(signedAngle: number): number {
    let angle = -signedAngle;
    if (angle < 0) {
        angle += MAX_ANGLE;
    }
    if (Math.abs(angle - MAX_ANGLE) <= 1e-6) {
        return 0;
    }
    return Math.min(MAX_ANGLE, Math.max(0, angle));
}

/**
 * 将角度展开到接近参考角的分支。
 *
 * @param signedAngle 有符号角。
 * @param referenceAngle 参考角。
 * @returns 展开后的角。
 */
function unwrapAngleNearReference(signedAngle: number, referenceAngle: number): number {
    let angle = signedAngle;
    while (angle - referenceAngle > Math.PI) {
        angle -= MAX_ANGLE;
    }
    while (angle - referenceAngle < -Math.PI) {
        angle += MAX_ANGLE;
    }
    return angle;
}
